package com.timacookies.components.sections

import androidx.compose.runtime.Composable
import com.timacookies.AppStrings
import com.timacookies.Loc
import com.timacookies.currentLanguage
import com.timacookies.localizedStrings
import com.timacookies.toSitePalette
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.css.svw
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.textTransform
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh

val FooterStyle = CssStyle.base {
  Modifier
    .backgroundColor(colorMode.toSitePalette().background.secondary)
    .padding(top = 10.vh, bottom = 5.vh, leftRight = 10.percent)
}

@Composable
fun Footer(modifier: Modifier = Modifier) {
  val strings = localizedStrings()
  val lang = currentLanguage()

  Column(
    modifier = FooterStyle.toModifier().then(modifier).gap(6.cssRem),
    horizontalAlignment = Alignment.CenterHorizontally,
  ) {
    // Brand Header
    Column(
      horizontalAlignment = Alignment.CenterHorizontally,
      modifier = Modifier.gap(1.5.cssRem),
    ) {
      // Smaller brand title on mobile, full size on MD+
      SpanText(
        "TIMA COOKIES",
        Modifier
          .fontFamily("Noto Serif Display")
          .textAlign(TextAlign.Center)
          .fontSize(2.cssRem)
          .letterSpacing(0.2.cssRem)
          .displayUntil(Breakpoint.SM),
      )
      SpanText(
        "TIMA COOKIES",
        Modifier
          .fontFamily("Noto Serif Display")
          .textAlign(TextAlign.Center)
          .fontSize(3.cssRem)
          .letterSpacing(0.4.cssRem)
          .displayIfAtLeast(Breakpoint.SM),
      )
      SpanText(
        strings.heroDetail,
        Modifier
          .fontFamily("Montserrat", "sans-serif")
          .fontSize(1.cssRem)
          .fontWeight(FontWeight.Light)
          .letterSpacing(0.3.cssRem)
          .textTransform(TextTransform.Uppercase)
          .opacity(0.5),
      )
    }

    // Multi-column section — stacked on mobile, side-by-side on SM+
    Column(
      Modifier.displayUntil(Breakpoint.SM).fillMaxWidth().gap(3.cssRem),
      horizontalAlignment = Alignment.CenterHorizontally,
    ) {
      FooterSocialColumn(strings)
      FooterContactColumn(strings)
    }

    Row(
      Modifier.displayIfAtLeast(Breakpoint.SM).fillMaxWidth().maxWidth(60.cssRem).gap(4.svw),
      horizontalArrangement = Arrangement.SpaceEvenly,
      verticalAlignment = Alignment.Top,
    ) {
      FooterSocialColumn(strings)
      FooterContactColumn(strings)
    }

    Box(
      Modifier
        .fillMaxWidth()
        .height(1.px)
        .opacity(0.05),
    )

    // Bottom row
    Row(
      Modifier.fillMaxWidth(),
      horizontalArrangement = Arrangement.SpaceBetween,
      verticalAlignment = Alignment.CenterVertically,
    ) {
      Row(Modifier.gap(2.cssRem), verticalAlignment = Alignment.CenterVertically) {
        SpanText(
          "© 2026 TIMA COOKIES",
          Modifier.fontSize(0.75.cssRem).opacity(0.4).letterSpacing(0.1.cssRem),
        )
        Link(
          Loc.path(lang, "/privacy"),
          strings.privacyPolicy,
          variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
          modifier = Modifier.fontSize(0.75.cssRem).opacity(0.4).letterSpacing(0.1.cssRem),
        )
      }
    }
  }
}

@Composable
private fun FooterSocialColumn(strings: AppStrings) {
  Column(Modifier.gap(1.5.cssRem), horizontalAlignment = Alignment.CenterHorizontally) {
    SpanText(
      strings.socialTitle,
      Modifier.fontSize(0.9.cssRem).letterSpacing(0.2.cssRem).opacity(0.6),
    )
    Column(
      Modifier.gap(0.8.cssRem),
      horizontalAlignment = Alignment.CenterHorizontally,
    ) {
      val socialLinkModifier = Modifier
        .fontFamily("Montserrat")
        .fontSize(0.9.cssRem)
        .fontWeight(FontWeight.Light)
        .opacity(0.8)
      Link(
        "https://www.instagram.com/timas_cookies",
        "Instagram",
        variant = UncoloredLinkVariant,
        modifier = socialLinkModifier,
      )
      Link(
        "https://www.youtube.com/@TimaCookies",
        "YouTube",
        variant = UncoloredLinkVariant,
        modifier = socialLinkModifier,
      )
      Link(
        "https://www.tiktok.com/@timascookies",
        "TikTok",
        variant = UncoloredLinkVariant,
        modifier = socialLinkModifier,
      )
      Link(
        "https://www.facebook.com/timascookies",
        "Facebook",
        variant = UncoloredLinkVariant,
        modifier = socialLinkModifier,
      )
      Link(
        "https://www.pinterest.com/tima_cookies/",
        "Pinterest",
        variant = UncoloredLinkVariant,
        modifier = socialLinkModifier,
      )
    }
  }
}

@Composable
private fun FooterContactColumn(strings: AppStrings) {
  Column(Modifier.gap(1.5.cssRem), horizontalAlignment = Alignment.CenterHorizontally) {
    SpanText(
      strings.contactTitle,
      Modifier.fontSize(0.9.cssRem).letterSpacing(0.2.cssRem).opacity(0.6),
    )
    Column(
      Modifier.gap(0.8.cssRem),
      horizontalAlignment = Alignment.CenterHorizontally,
    ) {
      val contactTextModifier = Modifier
        .fontFamily("Montserrat")
        .fontSize(0.9.cssRem)
        .fontWeight(FontWeight.Light)
        .opacity(0.8)
      SpanText("Zurich, Switzerland", contactTextModifier)
      Link(
        "mailto:timacookies.ch@gmail.com",
        "timacookies.ch@gmail.com",
        variant = UncoloredLinkVariant,
        modifier = contactTextModifier,
      )
      Link(
        "tel:+41779761531",
        "+41 77 976 15 31",
        variant = UncoloredLinkVariant,
        modifier = contactTextModifier,
      )
    }
  }
}
